import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildSnapshot, type WidgetSnapshot } from "@/lib/widget-data";
import { pushSnapshot, publishAuthForWidgets } from "@/lib/widget-bridge";
import { WIDGET_CONFIG_KEY, enabledModules, readWidgetConfig } from "@/lib/widget-config";

/**
 * Tiene aggiornati i widget nativi della home del telefono.
 *
 * Ricalcola lo snapshot quando cambiano streak/XP, timer o prossime scadenze,
 * al rientro in primo piano e a intervalli regolari mentre l'app è aperta.
 * I widget continuano poi ad aggiornarsi da soli anche ad app chiusa usando
 * l'ultimo snapshot salvato più il rifornimento in background nativo.
 */

const TIMER_KEY = "lumen.timer.v1";
const REFRESH_MS = 5 * 60 * 1000;

type PersistedTimer = {
  mode?: string;
  preset?: number;
  runningSince?: number | null;
  accumulatedSec?: number;
};

function readTimer(now: number) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(TIMER_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as PersistedTimer;
    const running = typeof t.runningSince === "number" && t.runningSince > 0;
    const live = running ? Math.max(0, Math.floor((now - (t.runningSince as number)) / 1000)) : 0;
    const elapsedSec = (typeof t.accumulatedSec === "number" ? t.accumulatedSec : 0) + live;
    const mode = typeof t.mode === "string" ? t.mode : "pomodoro";
    const countdown = mode === "pomodoro" || mode === "countdown";
    const total = (typeof t.preset === "number" ? t.preset : 25) * 60;
    return {
      running,
      mode,
      elapsedSec,
      remainingSec: countdown ? Math.max(0, total - elapsedSec) : null,
      endsAt: null,
    };
  } catch {
    return null;
  }
}

export function useWidgetSync() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const busy = useRef(false);

  const sync = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;

      // I moduli spenti non vengono nemmeno interrogati: meno dati, widget più snelli.
      const config = readWidgetConfig();
      const modules = enabledModules(config);
      const wantsAgenda = modules.includes("agenda") || modules.includes("reminder");
      const wantsProgress = modules.includes("progress") || modules.includes("reminder");
      const wantsTimer = modules.includes("timer");

      const nowIso = new Date().toISOString();
      const [{ data: profile }, { data: events }, { data: homework }] = await Promise.all([
        wantsProgress
          ? supabase.from("profiles").select("streak_days,total_xp,level").eq("id", u.user.id).maybeSingle()
          : Promise.resolve({ data: null }),
        !wantsAgenda
          ? Promise.resolve({ data: [] })
          : supabase
          .from("calendar_events")
          .select("title,event_type,subject_name,starts_at")
          .gte("starts_at", nowIso)
          .order("starts_at")
          .limit(5),
        !wantsAgenda
          ? Promise.resolve({ data: [] })
          : supabase
          .from("homework")
          .select("title,subject,due_date,done")
          .eq("done", false)
          .order("due_date")
          .limit(3),
      ]);

      const quotes = t("widgets.quotes", { returnObjects: true }) as unknown;
      const list = Array.isArray(quotes) ? (quotes as string[]) : [];
      const quote = list.length ? list[new Date().getDate() % list.length]! : "";

      const snapshot: WidgetSnapshot = buildSnapshot({
        language: i18n.language,
        profile,
        quote: modules.includes("reminder") ? quote : "",
        timer: wantsTimer ? readTimer(Date.now()) : null,
        modules,
        sourceRole: config.sourceRole,
        events: events ?? [],
        alerts: (homework ?? [])
          .map((h) => `${h.subject ? `${h.subject}: ` : ""}${h.title ?? ""}`.trim())
          .filter(Boolean),
      });

      await pushSnapshot(snapshot);
      await publishAuthForWidgets();
    } catch (error) {
      console.error("widget sync failed", error);
    } finally {
      busy.current = false;
    }
  }, [t, i18n.language]);

  useEffect(() => {
    void sync();
    const timer = window.setInterval(() => void sync(), REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === TIMER_KEY || e.key === WIDGET_CONFIG_KEY) void sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("storage", onStorage);
    window.addEventListener("lumen:widget-config", onVisible);
    // Ogni volta che la cache dell'app cambia (XP, streak, scadenze) riallineiamo i widget.
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = String(event.query?.queryKey?.[0] ?? "");
      if (["profile", "profile-flags", "quests", "widget-data", "planner", "calendar", "account-type"].includes(key)) {
        void sync();
      }
    });
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("lumen:widget-config", onVisible);
      unsubscribe();
    };
  }, [sync, queryClient]);

  return sync;
}
